<template>
  <div id="app" class="h-100" :class="[skinClasses]">

    <component  :is="layout">
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />
    <!-- <message-box v-if="$route.meta.showMessageBox" /> -->
  </div>
</template>

<script>
import MessageBox from "@core/components/message-box/MessageBox.vue";
import ScrollToTop from "@core/components/scroll-to-top/ScrollToTop.vue";
import VuexyLogo from '@core/layouts/components/Logo.vue'


// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";
import { useWindowSize, useCssVar } from "@vueuse/core";
import { Hub, Auth } from "aws-amplify";
import store from "@/store";
import axios from "axios";
import Vue from "vue";

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutHorizontal = () =>
  import("@/layouts/horizontal/LayoutHorizontal.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

    MessageBox,
    ScrollToTop,
    VuexyLogo,
  },
  setup() {
    // version check
    const channel = new BroadcastChannel("refresh_channel");
    channel.onmessage = (event) => {
      if (event.data === "refresh") {
        window.location.reload();
      }
    };
    axios
      .get(Vue.prototype.$mainConfig.api.baseURL + "/global/version")
      .then(({ data }) => {
        const lastVersion = data.adinVersion;
        const currentVersion = store.getters["adinSettings/getVersion"];
        console.log(
          "lastVersion: " + lastVersion + " currentVersion: " + currentVersion
        );
        if (!data.ignore && lastVersion !== currentVersion) {
          // Clear cookies
          document.cookie.split(";").forEach((c) => {
            document.cookie = c
              .replace(/^ +/, "")
              .replace(
                /=.*/,
                "=;expires=" + new Date().toUTCString() + ";path=/"
              );
          });

          // Clear localStorage
          localStorage.clear();

          // Clear sessionStorage
          sessionStorage.clear();
          sessionStorage.setItem("adinVersion", lastVersion);
          channel.postMessage("refresh");
          window.location.reload();
        }
        sessionStorage.setItem("adinVersion", lastVersion);
      })
      .catch((e) => {
        console.log("Version check failed", e);
      });

    const { skin, skinClasses } = useAppConfig();
    const { enableScrollToTop } = $themeConfig.layout;

    // If skin is dark when initialized => Add class to body
    if (skin.value === "dark") document.body.classList.add("dark-layout");
    if (skin.value === "adin") document.body.classList.add("adin-layout");

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade",
    });

    // Set Window Width in store
    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });

    return {
      skinClasses,
      enableScrollToTop,
    };
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === "full") return "layout-full";
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      "primary",
      "secondary",
      "success",
      "info",
      "warning",
      "danger",
      "light",
      "dark",
    ];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
  },
  async created() {
    // FOR DEV, NOT FOR PRODUCTION
    Hub.listen("auth", (data) => {
      switch (data.payload.event) {
        case "signIn":
          console.log("HUB: user signed in");
          break;
        case "signUp":
          console.log("HUB: user signed up");
          break;
        case "signOut":
          console.log("HUB: user signed out");
          break;
        case "signIn_failure":
          console.log("HUB: user sign in failed");
          break;
        case "configured":
          console.log("HUB: the Auth module is configured");
          break;
        case "tokenRefresh":
          console.log("HUB: token refresh succeeded");
          break;
        case "tokenRefresh_failure":
          console.log("HUB: tokenRefresh_failure");
          // localStorage.clear();
          // this.$router.replace({ name: "auth-login" });
          break;
        case "forgotPasswordSubmit_failure":
          /*    try {
            this.$toast({
              component: ToastificationContent,
              props: {
                text: data.payload.data.message,
                title: 'Error when resetting password',
                icon: 'CrossIcon',
                variant: 'danger'
              }
            })
          } catch (e) {
            console.log(e)
            this.$toast({
              component: ToastificationContent,
              props: {
                text: "There is an error when resetting password",
                title: 'Error',
                icon: 'CrossIcon',
                variant: 'danger'
              }
            })
          }*/
          break;
        default:
          console.log(`HUB: ${data.payload.event}`);
          break;
      }
    });
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/dashboard-ecommerce.scss";
@import "@core/scss/vue/libs/chart-apex.scss";
@import "@core/scss/vue/libs/vue-select.scss";
</style>
